import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import { CASE_STUDIES, IMG } from "@/lib/site";
import SEO from "@/components/SEO";

export default function CaseStudies() {
  return (
    <div data-testid="case-studies-page">
      <SEO
        title="Case Studies"
        description="Recent concrete repair surveys and diagnostic investigations across the UK, basement beams, office columns, car park waffle slabs and slab soffits. Observation, investigation and recommendation."
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// Projects · Survey &amp; Diagnostic Records</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            Recent surveys<br />&amp; investigations.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            A selection of recent diagnostic commissions, from car park
            decks to basement beams. Every job starts with a defect survey,
            corrosion assessment and a costed recommendation.
          </p>
        </div>
      </section>

      {/* Case studies list */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="space-y-24">
          {CASE_STUDIES.map((cs, i) => {
            const flip = i % 2 === 1;
            return (
              <article
                key={cs.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                data-testid={`case-study-${cs.id}`}
              >
                <div className={`lg:col-span-7 relative ${flip ? "lg:order-2" : ""}`}>
                  <div className="relative border border-zinc-900 overflow-hidden">
                    <img src={IMG[cs.image]} alt={cs.title} loading="lazy" className="w-full h-auto block" />
                    <div className="absolute top-3 left-3 font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-100 bg-zinc-950/80 px-2 py-1 border border-zinc-100/20">
                      REF · {cs.ref}
                    </div>
                    <div className="absolute bottom-3 right-3 font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-100 bg-[#C2410C] px-2 py-1">
                      {cs.tag}
                    </div>
                  </div>
                </div>

                <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:pr-8" : "lg:pl-4"}`}>
                  <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                    <span className="flex items-center gap-1.5"><MapPin size={12} /> {cs.location}</span>
                    <span className="flex items-center gap-1.5"><Calendar size={12} /> {cs.year}</span>
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
                    {cs.title}
                  </h2>
                  <div className="rule-line my-6 bg-zinc-900" />

                  <div className="space-y-6 text-zinc-700">
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C2410C]">// Observation</div>
                      <p className="mt-2 leading-relaxed">{cs.problem}</p>
                    </div>
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C2410C]">// Investigation</div>
                      <p className="mt-2 leading-relaxed">{cs.solution}</p>
                    </div>
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C2410C]">// Recommendation</div>
                      <p className="mt-2 leading-relaxed">{cs.outcome}</p>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative border border-zinc-900 p-10 md:p-16 bg-zinc-50">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />
          <div className="tech-label">// Your project</div>
          <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
            Could be next on our books.
          </h3>
          <p className="mt-6 max-w-2xl text-zinc-700">
            Send us a photograph, a location and a short description. We&apos;ll
            come back with a scope of investigation and a view on likely
            repair strategy.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="case-studies-cta-enquire">
              Start an enquiry <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
